import React, { useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { formatApiErrorDetail } from "@/lib/api";
import { hoyISO } from "@/lib/zona";
import { abrirVisor } from "@/lib/visor";
import { CATEGORIAS_GASTO, dec2, parseImporte } from "@/lib/gastos";
import { Sheet } from "@/pages/Familia";
import { Avatar } from "@/components/personas/Avatar";

const input = "w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/15";
const val = (d, k) => (d && d[k] && d[k].valor != null && d[k].valor !== "" ? d[k].valor : null);
const dudoso = (d, k) => val(d, k) != null && (d[k].confianza ?? 0) < 0.6;

const Campo = ({ label, children, aviso, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="text-sm font-semibold text-[#4A5550]">{label}</span>
    <div className="mt-1">{children}</div>
    {aviso && <span className="block text-sm text-[#B45309] mt-1">{aviso}</span>}
  </label>
);

const inicialDe = (g, d) => g ? {
  ...g, importe: dec2(g.importe), asociado: g.vehiculo_id ? `vehiculo:${g.vehiculo_id}` : g.vivienda_id ? `vivienda:${g.vivienda_id}` : g.pet_id ? `mascota:${g.pet_id}` : "",
} : {
  // Gastos = dinero ya pagado: una fecha leída en el futuro (viaje, vencimiento) no se usa; se propone hoy.
  fecha: /^\d{4}-\d{2}-\d{2}$/.test(val(d, "fecha") || "") && val(d, "fecha") <= hoyISO() ? val(d, "fecha") : hoyISO(),
  importe: val(d, "importe_total") != null ? dec2(val(d, "importe_total")) : "",
  proveedor: val(d, "proveedor") || "", concepto: val(d, "concepto") || "", categoria: val(d, "categoria") || "",
  forma_pago: val(d, "forma_pago"), impuestos: val(d, "impuestos"), family_member_id: "", asociado: "", notas: "",
};

// Formulario de gasto: siempre se confirma o corrige antes de guardar.
export const GastoForm = ({ gasto, docId, detectado, duplicado, personaInicial, asociadoInicial, origen, onClose, onSaved, onDiferido }) => {
  const { familyMembers, viviendas, vehiculos, pets, guardarGasto } = useData();
  const [f, setF] = useState(() => {
    const ini = inicialDe(gasto, detectado);
    if (!gasto && personaInicial) ini.family_member_id = personaInicial;
    if (!gasto && asociadoInicial) ini.asociado = asociadoInicial;
    if (!gasto && ini.categoria === "combustible" && vehiculos.length === 1) ini.asociado = `vehiculo:${vehiculos[0].id}`;
    return ini;
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const doc = gasto?.documento_id || docId;
  const leido = !!detectado && !gasto;
  const noAparece = (k) => (leido && val(detectado, k) == null ? "No aparece en el ticket: complétalo tú." : leido && dudoso(detectado, k) ? "Compruébalo: no se lee con claridad." : null);
  const opciones = [
    ...vehiculos.map((v) => [`vehiculo:${v.id}`, `Vehículo · ${v.alias}`]),
    ...viviendas.map((v) => [`vivienda:${v.id}`, `Vivienda · ${v.alias}`]),
    ...pets.map((p) => [`mascota:${p.id}`, `Mascota · ${p.name}`]),
  ];

  const guardar = async () => {
    const importe = parseImporte(f.importe);
    if (!importe || importe <= 0) return toast.error("Indica el importe del gasto.");
    if (!f.categoria) return toast.error("Elige una categoría.");
    if (f.fecha > hoyISO() && f.fecha !== gasto?.fecha) return toast.error("Un gasto es dinero ya pagado: la fecha no puede ser posterior a hoy. Si aún no lo has pagado, créalo en «Pagos y recordatorios periódicos».");
    const [tipo, id] = (f.asociado || ":").split(":");
    const payload = {
      fecha: f.fecha, importe, proveedor: f.proveedor, concepto: f.concepto, categoria: f.categoria, notas: f.notas,
      family_member_id: f.family_member_id || null, vehiculo_id: tipo === "vehiculo" ? id : null, vivienda_id: tipo === "vivienda" ? id : null,
      pet_id: tipo === "mascota" ? id : null, documento_id: doc || null, forma_pago: f.forma_pago || null,
      impuestos: f.impuestos != null && f.impuestos !== "" ? parseImporte(f.impuestos) : null, origen,
    };
    if (onDiferido) return onDiferido(payload);
    setBusy(true);
    try {
      const g = await guardarGasto(gasto?.id, payload);
      toast.success(gasto ? "Gasto actualizado" : "Gasto registrado");
      onSaved?.(g);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar el gasto.");
    } finally { setBusy(false); }
  };

  return (
    <Sheet onClose={onClose} testid="gasto-form">
      <h2 className="font-heading text-2xl font-bold text-[#1C2421] pr-12">{onDiferido ? "Datos del gasto" : gasto ? "Editar gasto" : leido ? "He leído el ticket" : "Nuevo gasto"}</h2>
      {onDiferido ? <p className="text-base text-[#4A5550] mt-1" data-testid="gasto-leido-aviso">Revisa el importe y los datos. El gasto se registrará cuando confirmes el documento.</p>
        : leido && <p className="text-base text-[#4A5550] mt-1" data-testid="gasto-leido-aviso">Revisa los datos y corrígelos si hace falta. No se guarda nada hasta que pulses «Guardar gasto».</p>}
      {duplicado && (
        <p data-testid="gasto-duplicado" className="mt-3 bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-3 text-sm text-[#9A3412] flex gap-2">
          <Icons.Files size={18} className="shrink-0" /> Parece que este ticket ya está guardado ({duplicado.filename}). Revísalo para no contarlo dos veces; si es repetido, cierra sin guardar.
        </p>
      )}
      {doc && (
        <button type="button" data-testid="gasto-ver-justificante" onClick={() => abrirVisor({ id: doc, title: "Justificante" })}
          className="mt-3 w-full min-h-[48px] rounded-xl bg-[#E8F0EC] text-[#2E5645] font-semibold inline-flex items-center justify-center gap-2">
          <Icons.Paperclip size={18} /> Ver el ticket o factura
        </button>
      )}
      <div className="space-y-4 mt-5">
        <Campo label="Importe total (€)" aviso={noAparece("importe_total")}>
          <input data-testid="gasto-importe" inputMode="decimal" value={f.importe} onChange={(e) => set("importe")(e.target.value)}
            placeholder="0,00" className={`${input} text-2xl font-bold`} />
        </Campo>
        <Campo label="Fecha del pago" aviso={noAparece("fecha")}>
          <input data-testid="gasto-fecha" type="date" max={gasto?.fecha > hoyISO() ? gasto.fecha : hoyISO()} value={f.fecha} onChange={(e) => set("fecha")(e.target.value)} className={input} />
        </Campo>
        <Campo label="Establecimiento o proveedor" aviso={noAparece("proveedor")}>
          <input data-testid="gasto-proveedor" value={f.proveedor} onChange={(e) => set("proveedor")(e.target.value)} placeholder="Ej. Mercadona, Repsol…" className={input} />
        </Campo>
        <Campo label="Concepto">
          <input data-testid="gasto-concepto" value={f.concepto} onChange={(e) => set("concepto")(e.target.value)} placeholder="Ej. Compra semanal, repostaje…" className={input} />
        </Campo>
        <div>
          <p className="text-sm font-semibold text-[#4A5550]">Categoría{leido && val(detectado, "categoria") ? " (sugerida, puedes cambiarla)" : ""}</p>
          <div className="grid grid-cols-2 gap-2 mt-2" role="radiogroup" aria-label="Categoría">
            {CATEGORIAS_GASTO.map((c) => {
              const I = Icons[c.icon] || Icons.Receipt; const sel = f.categoria === c.id;
              return (
                <button key={c.id} type="button" role="radio" aria-checked={sel} data-testid={`gasto-cat-${c.id}`} onClick={() => set("categoria")(c.id)}
                  className={`min-h-[52px] px-3 rounded-xl border-2 text-sm font-semibold inline-flex items-center gap-2 text-left ${sel ? "bg-[#3A6B56] border-[#3A6B56] text-white" : "bg-white border-[#E5DFD5] text-[#1C2421]"}`}>
                  <I size={18} className="shrink-0" /> {c.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold text-[#4A5550]">¿De quién es el gasto? <span className="font-normal text-[#6E7A74]">(opcional)</span></p>
          <div className="flex flex-wrap gap-2 mt-2">
            <button type="button" data-testid="gasto-persona-ninguna" onClick={() => set("family_member_id")("")}
              className={`min-h-[48px] px-4 rounded-xl border-2 text-sm font-semibold ${!f.family_member_id ? "bg-[#3A6B56] border-[#3A6B56] text-white" : "bg-white border-[#E5DFD5]"}`}>Sin asignar</button>
            {familyMembers.map((p) => (
              <button key={p.id} type="button" data-testid={`gasto-persona-${p.id}`} onClick={() => set("family_member_id")(p.id)}
                className={`min-h-[48px] pl-1.5 pr-4 rounded-xl border-2 text-sm font-semibold inline-flex items-center gap-2 ${f.family_member_id === p.id ? "bg-[#3A6B56] border-[#3A6B56] text-white" : "bg-white border-[#E5DFD5]"}`}>
                <Avatar persona={p} size="sm" /> <span aria-hidden="true">{p.name}</span>
              </button>
            ))}
          </div>
        </div>
        {opciones.length > 0 && (
          <Campo label="Asociarlo a una vivienda, vehículo o mascota (opcional)">
            <select data-testid="gasto-asociado" value={f.asociado} onChange={(e) => set("asociado")(e.target.value)} className={input}>
              <option value="">No asociar</option>
              {opciones.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Campo>
        )}
        {(f.forma_pago || f.impuestos != null) && (
          <div className="grid grid-cols-2 gap-3">
            {f.forma_pago != null && <Campo label="Forma de pago"><input data-testid="gasto-forma-pago" value={f.forma_pago || ""} onChange={(e) => set("forma_pago")(e.target.value)} className={input} /></Campo>}
            {f.impuestos != null && <Campo label="IVA / impuestos (€)"><input data-testid="gasto-impuestos" inputMode="decimal" value={String(f.impuestos).replace(".", ",")} onChange={(e) => set("impuestos")(e.target.value)} className={input} /></Campo>}
          </div>
        )}
        <Campo label="Notas (opcional)">
          <textarea data-testid="gasto-notas" rows={2} value={f.notas || ""} onChange={(e) => set("notas")(e.target.value)} className={`${input} py-3`} />
        </Campo>
      </div>
      <button data-testid="gasto-guardar" disabled={busy} onClick={guardar}
        className="mt-6 w-full min-h-[56px] rounded-2xl bg-[#3A6B56] text-white font-bold text-lg disabled:opacity-60">
        {busy ? "Guardando…" : onDiferido ? "Usar estos datos" : "Guardar gasto"}
      </button>
    </Sheet>
  );
};
